import { isSameDay } from 'date-fns';
import {
  CompetitionGenderEnum,
  EventGroupFormatsEnum,
  GetGamedayByEventIdQuery,
  ScoringFormatEnum,
  TeamTypesEnum,
} from 'types/generated/client';
import { getGroupFormatName } from 'utils/shared/sports/getGroupFormatName';
import { getNavigatorLanguage } from 'utils/shared/time/getNavigatorLanguage';
import {
  formatDate,
  formatTime,
  formatWeekday,
} from 'utils/shared/time/formatEventDateTime';
import { FilterOption } from 'components/FilterDropdown';
import { EventProps } from './types';

export { getGroupFormatName, formatDate, formatTime, formatWeekday };

export const DEFAULT_TIMEZONE = 'America/Denver';

export function formatDateRange({
  startDate,
  endDate,
  timeZone,
  locale,
}: {
  startDate: string;
  endDate: string;
  timeZone?: string;
  locale?: string;
}) {
  const localeForDisplay = getNavigatorLanguage(locale);
  const optionsMonthDay: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone };
  const startDateObject = new Date(startDate);
  const endDateObject = new Date(endDate);

  const formattedStartDate = new Intl.DateTimeFormat(localeForDisplay, optionsMonthDay).format(startDateObject);
  const formattedEndDate = new Intl.DateTimeFormat(localeForDisplay, optionsMonthDay).format(endDateObject);

  if (isSameDay(startDateObject, endDateObject)) {
    return `${formattedStartDate}, ${startDateObject.getFullYear()}`;
  }

  return `${formattedStartDate} - ${formattedEndDate}, ${startDateObject.getFullYear()}`;
}

export const isShowGroupFormat = (groupFormat?: EventGroupFormatsEnum | null) => {
  return !!groupFormat && groupFormat !== EventGroupFormatsEnum.Custom;
};

export const isShowScoringFormat = (scoringFormat?: ScoringFormatEnum | null) => {
  return !!scoringFormat;
};

const scoringFormatToName = {
  [ScoringFormatEnum.Rally]: 'Rally scoring',
  [ScoringFormatEnum.Traditional]: 'Traditional scoring',
};

export const getScoringFormatName = (scoringFormat: ScoringFormatEnum) => {
  return scoringFormatToName[scoringFormat];
};

/**
 * Checks if all matches with scores in a VAIR round robin event have been submitted to VAIR
 * @param gamedayData - The gameday query data
 * @param isVairRoundRobin - Whether the event is a VAIR round robin format
 * @returns true if all matches with scores are submitted to VAIR, false otherwise
 */
export const areAllMatchesSubmittedToVair = (
  gamedayData: GetGamedayByEventIdQuery | null | undefined,
  isVairRoundRobin: boolean,
): boolean => {
  if (!isVairRoundRobin || !gamedayData?.eventsByPk?.groups) {
    return false;
  }

  for (const group of gamedayData.eventsByPk.groups) {
    if (group.format !== EventGroupFormatsEnum.VairRoundRobin) continue;

    for (const sequence of group.sequences || []) {
      for (const pool of sequence.pools || []) {
        for (const round of pool.rounds || []) {
          for (const match of round.matches || []) {
            // Check if match has scores (is complete) and is submitted to VAIR
            const hasScores = match.games?.some(
              (game) => typeof game.team1Score === 'number' && typeof game.team2Score === 'number',
            );

            // If match has scores but is not submitted to VAIR, return false
            if (hasScores && !match.isVairScoreSubmitted) {
              return false;
            }
          }
        }
      }
    }
  }

  // Check if there are any matches with scores at all
  let hasAnyMatchesWithScores = false;
  for (const group of gamedayData.eventsByPk.groups) {
    if (group.format !== EventGroupFormatsEnum.VairRoundRobin) continue;

    for (const sequence of group.sequences || []) {
      for (const pool of sequence.pools || []) {
        for (const round of pool.rounds || []) {
          for (const match of round.matches || []) {
            const hasScores = match.games?.some(
              (game) => typeof game.team1Score === 'number' && typeof game.team2Score === 'number',
            );
            if (hasScores) {
              hasAnyMatchesWithScores = true;
              break;
            }
          }
          if (hasAnyMatchesWithScores) break;
        }
        if (hasAnyMatchesWithScores) break;
      }
      if (hasAnyMatchesWithScores) break;
    }
    if (hasAnyMatchesWithScores) break;
  }

  // Only return true if there are matches with scores and all are submitted
  return hasAnyMatchesWithScores;
};

// ─── Event group filter utilities ────────────────────────────────────────────

export const ALL_FILTER_VALUE = 'all' as const;

type EventGroup = EventProps['event']['groups'][0];

export const GENDER_LABEL_MAP: Record<CompetitionGenderEnum, string> = {
  [CompetitionGenderEnum.Male]: "Men's",
  [CompetitionGenderEnum.Female]: "Women's",
  [CompetitionGenderEnum.Mixed]: 'Mixed',
  [CompetitionGenderEnum.Coed]: 'Co-ed',
};

export const TYPE_LABEL_MAP: Record<TeamTypesEnum, string> = {
  [TeamTypesEnum.Doubles]: 'Doubles',
  [TeamTypesEnum.Singles]: 'Singles',
};

export function getLevelKey(group: EventGroup): string | null {
  if (group.minimumRating && group.maximumRating) {
    return `${group.minimumRating}-${group.maximumRating}`;
  }
  if (group.minimumRating) {
    return `${group.minimumRating}+`;
  }
  if (group.maximumRating) {
    return `Up to ${group.maximumRating}`;
  }
  return null;
}

export function getAgeKey(group: EventGroup): string | null {
  if (group.minimumAge && group.maximumAge) {
    return `${group.minimumAge}-${group.maximumAge}`;
  }
  if (group.minimumAge) {
    return `${group.minimumAge}+`;
  }
  if (group.maximumAge) {
    return `Under ${group.maximumAge}`;
  }
  return null;
}

export interface EventGroupFilters {
  gender: CompetitionGenderEnum | typeof ALL_FILTER_VALUE;
  type: TeamTypesEnum | typeof ALL_FILTER_VALUE;
  level: string;
  age: string;
}

export function filterEventGroups(groups: EventGroup[], filters: EventGroupFilters): EventGroup[] {
  return groups.filter((group) => {
    if (filters.gender !== ALL_FILTER_VALUE && group.gender !== filters.gender) {
      return false;
    }
    if (filters.type !== ALL_FILTER_VALUE && group.teamType !== filters.type) {
      return false;
    }
    if (filters.level !== ALL_FILTER_VALUE) {
      const levelKey = getLevelKey(group);
      if (levelKey !== filters.level) return false;
    }
    if (filters.age !== ALL_FILTER_VALUE) {
      const ageKey = getAgeKey(group);
      if (ageKey !== filters.age) return false;
    }
    return true;
  });
}

export function buildEventFilterOptions(groups: EventGroup[]) {
  // Levels and ages are data-derived since they have no fixed enum
  const levelSet = new Set<string>();
  const ageSet = new Set<string>();

  for (const group of groups) {
    const levelKey = getLevelKey(group);
    if (levelKey) levelSet.add(levelKey);

    const ageKey = getAgeKey(group);
    if (ageKey) ageSet.add(ageKey);
  }

  // Gender and type use all enum values for consistent dropdowns
  const genderOptions: FilterOption[] = [
    { value: ALL_FILTER_VALUE, label: 'All genders' },
    ...Object.values(CompetitionGenderEnum).map((g) => ({ value: g, label: GENDER_LABEL_MAP[g] })),
  ];

  const typeOptions: FilterOption[] = [
    { value: ALL_FILTER_VALUE, label: 'All types' },
    ...Object.values(TeamTypesEnum).map((t) => ({ value: t, label: TYPE_LABEL_MAP[t] })),
  ];

  const levelOptions: FilterOption[] = [
    { value: ALL_FILTER_VALUE, label: 'All levels' },
    ...Array.from(levelSet).map((l) => ({ value: l, label: l })),
  ];

  const ageOptions: FilterOption[] = [
    { value: ALL_FILTER_VALUE, label: 'All ages' },
    ...Array.from(ageSet).map((a) => ({ value: a, label: a })),
  ];

  return { genderOptions, typeOptions, levelOptions, ageOptions };
}

// VAIR registration helpers (used by Register flow)

/** Returns { title, message } for ModalVairInfo when user does not meet event age requirement, or null if they do. */
export function getVairAgeRestrictionMessage(
  event: EventProps['event'],
  vairAge: number | null | undefined,
): { title: string; message: string } | null {
  const hasRestriction = event.groups.some((g) => g.minimumAge != null || g.maximumAge != null);
  if (!hasRestriction) return null;

  const failingGroup = event.groups.find((g) => {
    const min = g.minimumAge ?? -Infinity;
    const max = g.maximumAge ?? Infinity;
    return (g.minimumAge != null || g.maximumAge != null) && (vairAge == null || vairAge < min || vairAge > max);
  });

  if (!failingGroup) return null;

  return {
    title: 'Age requirement',
    message:
      "It looks like you may not meet the age requirements for this event. If you are deemed ineligible, you won't be able to compete.",
  };
}

export function getVairGenderMismatchMessage(
  event: EventProps['event'],
  groupIds: string[],
  vairGender: string | null | undefined,
): { title: string; message: string } | null {
  if (!vairGender?.trim()) return null;
  const normalized = vairGender.trim().toLowerCase();
  const isVairMale = normalized === 'm' || normalized === 'male' || normalized.includes('male');
  const isVairFemale =
    normalized === 'f' || normalized === 'female' || normalized.includes('female');
  if (!isVairMale && !isVairFemale) return null;

  const mismatchedGroup = event.groups.find(
    (g) =>
      groupIds.includes(g.id) &&
      (g.gender === CompetitionGenderEnum.Male || g.gender === CompetitionGenderEnum.Female) &&
      ((g.gender === CompetitionGenderEnum.Male && isVairFemale) ||
        (g.gender === CompetitionGenderEnum.Female && isVairMale)),
  );
  if (!mismatchedGroup) return null;

  const eventGenderLabel = GENDER_LABEL_MAP[mismatchedGroup.gender];
  const yourGenderLabel = isVairMale ? "Men's" : "Women's";
  return {
    title: 'Heads up',
    message: `You're registering for a ${eventGenderLabel} event, but your VAIR profile shows ${yourGenderLabel}. No problem — you can still register. We just wanted to let you know.`,
  };
}

/** Returns { title, message } for ModalVairInfo when event requires VAIR vairified status but user is not vairified. */
export function getVairVairifiedRequiredMessage(
  isVairified: boolean | null | undefined,
  groups?: EventGroup[],
): { title: string; message: string } | null {
  if (isVairified === true) return null;

  const requiringGroups = groups?.filter((g) => g.isVairVairifiedRequired) ?? [];
  const groupTitles = requiringGroups.map((g) => g.title);

  const groupList =
    groupTitles.length === 0
      ? null
      : groupTitles.length === 1
      ? `"${groupTitles[0]}"`
      : groupTitles.length === 2
      ? `"${groupTitles[0]}" and "${groupTitles[1]}"`
      : `"${groupTitles.slice(0, -1).join('", "')}", and "${groupTitles[groupTitles.length - 1]}"`;

  const message = groupList
    ? `You must get VAIRified before the tournament to compete in ${groupList}.`
    : "You must get VAIRified before the tournament to compete.";

  return {
    title: 'VAIRified required',
    message,
  };
}
